import type { LeadCardResponse, MunicipalityMatch } from "@gioia365/contracts";
import type { Locale, MessageKey } from "@gioia365/i18n";
import { roughE164 } from "../lead-display";
import type { CardLead, LeadEdit } from "../lead-queries";

// I dati della scheda (piano M2 Task 18) senza interfaccia: le etichette dei campi di sistema, come si scrivono i
// valori, come si leggono i numeri scritti a mano, e per ogni campo la modifica che va al server con il modo di
// mostrarla subito (salvataggio ottimistico). Il server resta l'autorità: normalizza e valida ogni valore.

export type CardSection = LeadCardResponse["model"]["sections"][number];
export type CardField = CardSection["fields"][number];
export type PhoneSlot = { holder: "primary" | "second"; kind: "mobile" | "landline" };

/** Dove la testata porta con «Nota»: il riquadro «Nuova nota» della Timeline (Task 19). */
export const NOTE_TARGET_ID = "lead-card-note";

/** Le etichette dei campi di sistema, dai cataloghi (le stesse che il registro delle attività scrive). */
export const SYSTEM_FIELD_LABELS: Readonly<Record<string, MessageKey>> = {
  "system:first_name": "leadCard.fields.firstName",
  "system:last_name": "leadCard.fields.lastName",
  "system:salon_name": "leadCard.fields.salonName",
  "system:mobile": "leadCard.fields.mobile",
  "system:landline": "leadCard.fields.landline",
  "system:email": "leadCard.fields.email",
  "system:owner": "leadCard.fields.owner",
  "system:second_holder": "leadCard.fields.secondHolder",
  "system:city": "leadCard.fields.city",
  "system:province": "leadCard.fields.province",
  "system:region": "leadCard.fields.region",
  "system:country": "leadCard.fields.country",
};

/** Provincia, regione e nazione: vengono dalla città e non si scrivono mai da sé (decisione del 1° ottobre 2026). */
export const DERIVED_PLACE_REFS: ReadonlySet<string> = new Set(["system:province", "system:region", "system:country"]);

export const SECTION_TITLES: Readonly<Record<CardSection["key"], MessageKey>> = {
  contact: "leadCard.data.sections.contact",
  salon: "leadCard.data.sections.salon",
  territory: "leadCard.data.sections.territory",
};

/** I limiti dei testi del contratto (`leadPatchBodySchema`). */
export const TEXT_LIMITS = { name: 120, salonName: 200, email: 254, custom: 2000 } as const;

/** Un testo come lo salva il server: spazi in fondo tolti, spazi ripetuti uniti; vuoto come `null`. */
export function cleanText(value: string): string | null {
  const text = value.trim().replace(/\s+/g, " ");
  return text === "" ? null : text;
}

/** Il nome di una persona dai due campi, oppure `null`. */
function personName(firstName: string | null, lastName: string | null): string | null {
  return [firstName, lastName].filter((part) => part?.trim()).join(" ") || null;
}

/** Il nome del titolare e, se c'è, quello del secondo titolare (come la testata del mock-up). */
export function holderNames(lead: CardLead): { first: string | null; second: string | null } {
  return { first: personName(lead.firstName, lead.lastName), second: lead.secondHolder ? personName(lead.secondHolder.firstName, lead.secondHolder.lastName) : null };
}

/** Il telefono principale: il primo nell'ordine del server (cellulare del titolare, poi fisso, poi secondo titolare). */
export function mainPhone(lead: CardLead): string | null {
  return lead.phones[0]?.e164 ?? null;
}

export function phoneOf(lead: CardLead, slot: PhoneSlot): string | null {
  return lead.phones.find((phone) => phone.holder === slot.holder && phone.kind === slot.kind)?.e164 ?? null;
}

const NO_SECOND_HOLDER: NonNullable<CardLead["secondHolder"]> = { firstName: null, lastName: null, email: null, mobile: null };

/** Il Lead con il numero di un posto cambiato (o tolto), come lo mostra la scheda prima della risposta. */
function withPhone(lead: CardLead, slot: PhoneSlot, e164: string | null): CardLead {
  const others = lead.phones.filter((phone) => !(phone.holder === slot.holder && phone.kind === slot.kind));
  const phones = e164 ? [...others, { id: `pending-${slot.holder}-${slot.kind}`, holder: slot.holder, kind: slot.kind, e164 }] : others;
  const order = ["primary:mobile", "primary:landline", "second:mobile"];
  phones.sort((left, right) => order.indexOf(`${left.holder}:${left.kind}`) - order.indexOf(`${right.holder}:${right.kind}`));
  const secondHolder = slot.holder === "second" ? { ...(lead.secondHolder ?? NO_SECOND_HOLDER), mobile: e164 } : lead.secondHolder;
  return { ...lead, phones, secondHolder };
}

type TextColumn = "firstName" | "lastName" | "salonName" | "email";

export function textEdit(column: TextColumn, value: string | null): LeadEdit {
  const changes: LeadEdit["changes"] = {};
  changes[column] = value;
  return {
    changes,
    optimistic: (lead) => {
      const next = { ...lead };
      next[column] = value;
      return next;
    },
  };
}

/** Un posto dei telefoni: il numero come l'ha scritto la persona (il server lo normalizza), vuoto per toglierlo. */
export function phoneEdit(slot: PhoneSlot, composed: string | null): LeadEdit {
  const changes: LeadEdit["changes"] =
    slot.holder === "second" ? { phones: [{ holder: "second", kind: "mobile", value: composed }] } : { phones: [{ holder: "primary", kind: slot.kind, value: composed }] };
  return { changes, optimistic: (lead) => withPhone(lead, slot, composed ? roughE164(composed) : null) };
}

type SecondHolderPart = "firstName" | "lastName" | "email";

/** Un campo del secondo titolare: il contratto vuole nome, cognome ed email insieme, gli altri due restano com'erano. */
export function secondHolderEdit(lead: CardLead, part: SecondHolderPart, value: string | null): LeadEdit {
  const { firstName, lastName, email } = lead.secondHolder ?? NO_SECOND_HOLDER;
  const names = { firstName, lastName, email };
  names[part] = value;
  return {
    changes: { secondHolder: names },
    optimistic: (draft) => {
      const secondHolder = { ...(draft.secondHolder ?? NO_SECOND_HOLDER) };
      secondHolder[part] = value;
      return { ...draft, secondHolder };
    },
  };
}

/** «Togli il secondo titolare»: nome, cognome, email e cellulare escono insieme. */
export function secondHolderRemoval(): LeadEdit {
  return {
    changes: { secondHolder: null },
    optimistic: (lead) => ({ ...lead, secondHolder: null, phones: lead.phones.filter((phone) => phone.holder !== "second") }),
  };
}

/** Il luogo del Lead da un comune scelto: provincia, regione e nazione vengono dalla città. */
function placeOf(match: MunicipalityMatch): NonNullable<CardLead["place"]> {
  // L'etichetta è «Aci Castello (CT)»: il nome e la sigla della provincia.
  const parts = /^(.*) \(([^()]+)\)$/.exec(match.label);
  return {
    cityIstatCode: match.istatCode,
    cityName: parts?.[1] ?? match.label,
    provinceName: match.provinceName,
    provinceAbbreviation: parts?.[2] ?? "",
    regionName: match.regionName,
    countryCode: "IT",
  };
}

/** La città scelta (o tolta): con lei cambiano subito anche provincia, regione e nazione, come le salva il server. */
export function cityEdit(match: MunicipalityMatch | null): LeadEdit {
  return {
    changes: { cityIstatCode: match?.istatCode ?? null },
    optimistic: (lead) => ({ ...lead, place: match ? placeOf(match) : null, countryCode: match ? "IT" : null }),
  };
}

/** Il comune di adesso come lo vuole il selettore della città. */
export function matchOf(place: CardLead["place"]): MunicipalityMatch | null {
  if (!place) return null;
  return { istatCode: place.cityIstatCode, label: cityLabel(place), provinceName: place.provinceName, regionName: place.regionName };
}

/** La città come la scrive il mock-up: «Treviglio (BG)». */
export function cityLabel(place: NonNullable<CardLead["place"]>): string {
  return place.provinceAbbreviation ? `${place.cityName} (${place.provinceAbbreviation})` : place.cityName;
}

export function customEdit(key: string, value: number | string | null): LeadEdit {
  return { changes: { custom: { [key]: value } }, optimistic: (lead) => ({ ...lead, custom: { ...lead.custom, [key]: value } }) };
}

/**
 * Lo status: la scheda si rilegge dopo, perché uno status che blocca le chiamate chiude sul server la sequenza di chiamate
 * (`blocked_status`, piano M5, D8) e cambia «Chiama»: la sezione della sequenza e «Prossimo passo» seguono il server.
 */
export function statusEdit(status: CardLead["status"]): LeadEdit {
  return {
    changes: { statusId: status.id },
    optimistic: (lead) => ({ ...lead, status: { id: status.id, name: status.name, key: status.key, isDefault: status.isDefault } }),
    reloadAfter: true,
  };
}

/**
 * Un numero scritto a mano, con al massimo due decimali, anche con «€» e spazi, come lo scrive la lingua della persona.
 * Con virgola e punto insieme il separatore dei decimali è l'ultimo. Con uno solo dei due: davanti a gruppi di tre cifre
 * il separatore delle migliaia della lingua lo è davvero (in italiano «1.250» è 1250, in inglese «1,250»); altrimenti è
 * la virgola dei decimali («38,5» vale 38,5 in tutte e due). Soltanto in inglese gli zeri in fondo ai decimali non
 * contano, così «1.250» è 1,25. In italiano una virgola davanti a tre cifre («1,250», «1,000», «38,500», abitudine
 * inglese per le migliaia) resta un numero con tre decimali, che si rifiuta invece di salvare un numero sbagliato.
 * `null` per una casella vuota, `"invalid"` per il resto.
 */
export function parseDecimal(text: string, locale: Locale): number | null | "invalid" {
  const compact = text.replace(/[\s€]/g, "");
  if (compact === "") return null;
  const thousandsMark = locale === "en" ? "," : ".";
  const groupedThousands = locale === "en" ? /^-?\d{1,3}(,\d{3})+$/ : /^-?\d{1,3}(\.\d{3})+$/;
  let normalized: string;
  const lastComma = compact.lastIndexOf(",");
  const lastDot = compact.lastIndexOf(".");
  if (lastComma >= 0 && lastDot >= 0) {
    const decimal = lastComma > lastDot ? "," : ".";
    const thousands = decimal === "," ? "." : ",";
    normalized = compact.split(thousands).join("").replace(decimal, ".");
  } else if (groupedThousands.test(compact)) {
    normalized = compact.split(thousandsMark).join("");
  } else {
    normalized = compact.replace(",", ".");
  }
  // «1.250» in inglese è 1,25: gli zeri in fondo ai decimali non sono decimali in più. Mai in italiano (vedi sopra).
  if (locale === "en") {
    normalized = normalized.replace(/^(-?\d+)\.(\d*?)0+$/, (_match, whole: string, decimals: string) => (decimals ? `${whole}.${decimals}` : whole));
  }
  if (!/^-?\d+(\.\d{1,2})?$/.test(normalized)) return "invalid";
  const value = Number(normalized);
  // Il server tiene al più 12 cifre intere (la scala della colonna).
  return Number.isFinite(value) && Math.abs(value) < 1e12 ? value : "invalid";
}

const numberFormatters = new Map<string, Intl.NumberFormat>();

function numberFormatter(locale: Locale, kind: "number" | "currency" | "plain"): Intl.NumberFormat {
  const key = `${locale}|${kind}`;
  let formatter = numberFormatters.get(key);
  if (!formatter) {
    formatter =
      kind === "currency"
        ? new Intl.NumberFormat(locale, { style: "currency", currency: "EUR", useGrouping: "always" })
        : new Intl.NumberFormat(locale, { maximumFractionDigits: 2, useGrouping: kind === "number" ? "always" : false });
    numberFormatters.set(key, formatter);
  }
  return formatter;
}

/** Un numero da leggere, con il separatore delle migliaia anche a quattro cifre («1.250»). */
export function formatNumber(locale: Locale, value: number): string {
  return numberFormatter(locale, "number").format(value);
}

/** Un importo in euro con due decimali («38,00 €»). */
export function formatEuro(locale: Locale, value: number): string {
  return numberFormatter(locale, "currency").format(value);
}

/** Un numero da correggere nella casella: senza migliaia, con la virgola in italiano («42,5»). */
export function formatEditableNumber(locale: Locale, value: number): string {
  return numberFormatter(locale, "plain").format(value);
}

/** Il nome della nazione di un luogo: «Italia» dai cataloghi, le altre dal browser. */
export function countryName(locale: Locale, code: string, italy: string): string {
  if (code === "IT") return italy;
  try {
    return new Intl.DisplayNames([locale], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

/**
 * La nazione del Lead come la mostra la riga Nazione: quella del comune (Italia), altrimenti quella del Lead senza
 * comune (Svizzera per `CH`), altrimenti nessuna (questione aperta 120).
 */
export function leadCountryName(locale: Locale, lead: Pick<CardLead, "place" | "countryCode">, italy: string): string | null {
  const code = lead.place?.countryCode ?? lead.countryCode;
  return code ? countryName(locale, code, italy) : null;
}
